<!-- Weather Widget Component -->
<div class="col-12 col-lg-6">
    <!-- Display Current Weather Data -->

    <!-- No Coordinates State -->
    @if (!hasCoordinates()) {
        <div class="card h-100">
            <div class="card-body p-4 d-flex flex-column align-items-center justify-content-center text-center gap-2">
                <i class="bi bi-geo-alt fs-1 text-muted"></i>
                <h6 class="mb-0">No location set</h6>
                <p class="text-muted small mb-0">Add coordinates to see the weather for this location.</p>
            </div>
        </div>

    <!-- Loading State -->
    } @else if (isLoading()) {
        <div class="card h-100" aria-busy="true">
            <div class="card-body p-3">
                <span class="visually-hidden">Loading weather…</span>

                <div class="skeleton-loader skeleton-title mb-3"></div>

                <div class="row row-cols-2 row-cols-md-4 g-2 mb-3">
                    @for (block of [1, 2, 3, 4]; track block) {
                        <div class="col">
                            <div class="skeleton-loader py-4"></div>
                        </div>
                    }
                </div>

                <div class="skeleton-loader skeleton-text-sm mb-2"></div>
                <div class="skeleton-loader py-4 mb-2"></div>
                <div class="skeleton-loader py-4"></div>
            </div>
        </div>

    <!-- Error State -->
    } @else if (errorMessage()) {
        <div class="card h-100">
            <div class="card-body p-4 d-flex flex-column align-items-center justify-content-center text-center gap-2" role="alert">
                <i class="bi bi-cloud-slash fs-1 text-muted"></i>
                <h6 class="mb-0">Weather unavailable</h6>
                <p class="text-muted small mb-2">{{ errorMessage() }}</p>
                <button type="button" class="btn btn-sm btn-outline-primary" (click)="loadForecast()">
                    <i class="bi bi-arrow-clockwise me-1"></i>
                    Retry
                </button>
            </div>
        </div>
    }

    <!-- Weather Data State -->
    @else if(weatherData) {
        <div class="card h-100">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-start gap-2 mb-3">
                <h4 class="mb-0">
                    <i class="bi {{ conditionIcons[weatherData.current.conditions] ?? 'bi-cloud-sun-fill' }}" role="img" [attr.aria-label]="weatherData.current.conditions || 'Conditions unavailable'"></i>
                    Weather Forecast - {{ weatherData.current.observedAt | date:'MMM d' }}
                </h4>
                @if (locationLabel()) {
                    <span class="badge rounded-pill bg-primary-subtle text-primary-emphasis fw-medium d-inline-flex align-items-center gap-1 text-nowrap">
                        <i class="bi bi-geo-alt-fill"></i>
                        {{ locationLabel() }}
                    </span>
                }
            </div>

            <div class="row row-cols-2 row-cols-md-4 g-2">

                <!-- Temperature -->
                <div class="col">
                    <div class="border rounded p-2 h-100 text-center">
                    <i class="bi bi-thermometer-half text-primary"></i>
                    <div class="small text-muted">Temperature</div>
                    <div class="fs-5 fw-semibold">{{ weatherData.current.temperature }}°F</div>
                    </div>
                </div>

                <!-- Wind -->
                <div class="col">
                    <div class="border rounded p-2 h-100 text-center position-relative">
                        @if (isHazardousWind(weatherData.current.windSpeed)) {
                            <i class="bi bi-exclamation-triangle-fill text-warning rounded-circle lh-1 position-absolute top-0 start-100 translate-middle"
                            title="Wind may be too high for spraying"></i>
                        }
                        <i class="bi bi-wind text-primary"></i>
                        <div class="small text-muted">Wind</div>
                        <div class="fs-5"
                            [class.text-danger]="isHazardousWind(weatherData.current.windSpeed)"
                            [class.fw-semibold]="!isHazardousWind(weatherData.current.windSpeed)">
                        {{ weatherData.current.windSpeed }} mph {{ weatherData.current.windDirection }}
                        </div>
                    </div>
                </div>

        
                <!-- Precipitation -->
                <div class="col">
                    <div class="border rounded p-2 h-100 text-center">
                    <i class="bi bi-cloud-rain text-primary"></i>
                    <div class="small text-muted">Precipitation</div>
                    <div class="fs-5 fw-semibold">{{ weatherData.current.precipitation }} in</div>
                    </div>
                </div>
            

                <!-- Humidity -->
                <div class="col">
                    <div class="border rounded p-2 h-100 text-center">
                    <i class="bi bi-droplet text-primary"></i>
                    <div class="small text-muted">Humidity</div>
                    <div class="fs-5 fw-semibold">{{ weatherData.current.humidity }}%</div>
                    </div>
                </div>           
            </div>

            <!-- 12 Hour Forecast -->
            @if (!quickLook()) {
                <h6 class="text-muted small text-uppercase mt-3 mb-2">Next 12 hours</h6>

                <div class="d-flex gap-2 overflow-x-auto pt-2 pe-2 pb-2">
                    @for (period of weatherData.forecast; track period.time; let i = $index) {

                    <!-- Hour button -->
                    <button type="button"
                        class="btn btn-sm flex-shrink-0 d-flex flex-column align-items-center px-3 py-2 position-relative"
                        [class.btn-primary]="i === selectedHour()"
                        [class.btn-outline-secondary]="i !== selectedHour()"
                        [attr.aria-pressed]="i === selectedHour()"
                        (focus)="selectedHour.set(i)"
                        (click)="selectedHour.set(i)">

                        <!-- Hazardous condition indicator -->
                        @if (isHazardousHour(period)) {
                        <i class="bi bi-exclamation-triangle-fill text-warning rounded-circle lh-1 position-absolute top-0 start-100 translate-middle" title="Conditions may not be suitable for spraying"></i>
                        }

                        <!-- Time -->
                        <span class="small">{{ i === 0 ? 'Now' : (period.time | date:'h a') }}</span>

                        <!-- Weather icon -->
                        <i class="bi {{ conditionIcons[period.conditions] ?? 'bi-cloud' }} fs-5 my-1"></i>

                        <!-- Temperature -->
                        <span class="fw-semibold">{{ period.temperature }}°</span>
                    </button>
                    }
                </div>

                <!-- Selected hour detail -->
                @if (weatherData.forecast[selectedHour()]; as period) {
                <div class="border rounded p-3 mt-2 d-flex align-items-center gap-3">

                    <!-- Selected hour summary -->
                    <div class="d-flex align-items-center gap-2 flex-shrink-0">
                        <i class="bi {{ conditionIcons[period.conditions] ?? 'bi-cloud' }} text-primary fs-4"></i>
                        <div>
                            <div class="fw-semibold">{{ period.time | date:'h:mm a' }}</div>
                            <div class="small"
                                [class.text-danger]="isHazardousConditions(period.conditions)"
                                [class.text-muted]="!isHazardousConditions(period.conditions)">
                            {{ period.conditions || 'Conditions unavailable' }}
                            </div>
                        </div>
                    </div>

                    <div class="row g-2 flex-grow-1 small text-center">

                        <!-- Temperature -->
                        <div class="col">
                            <div class="text-muted">Temp</div>
                            <div class="fw-semibold">{{ period.temperature }}°F</div>
                        </div>

                        <!-- Wind -->
                        <div class="col">
                            <div class="text-muted">Wind</div>
                            <div class="text-nowrap"
                                [class.text-danger]="isHazardousWind(period.windSpeed)"
                                [class.fw-semibold]="!isHazardousWind(period.windSpeed)">
                            {{ period.windSpeed }} mph {{ period.windDirection }}
                            </div>
                        </div>

                        <!-- Rain chance -->
                        <div class="col">
                            <div class="text-muted">Rain chance</div>
                            <div class="fw-semibold">{{ period.precipitationProbability }}%</div>
                        </div>
                    </div>
                </div>
                }
            }
        </div>
        <div class="text-muted small mb-2 ms-3">*Weather is general for this location and may differ from conditions on site.</div>
      </div>
    }
 </div>